"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useProfile, type Profile } from "@/hooks/useProfile";
import { useAuth } from "@/components/auth-provider";

// ──────────────────────────────────────────────────────────────────────────
// ProfileSetupModal
// 프로필이 없으면 강제 노출되는 모달. 닫기 버튼 없음.
// 프리셋(수현/수진) 또는 직접 입력(이름 + 이모지 그리드)으로 선택.
// 선택 후 localStorage에 저장 → 자동 닫힘.
//
// 사용처:
//   1) 글로벌 자동 노출: <ProfileSetupGate /> (layout.tsx에 한 번)
//   2) 설정에서 "변경" 버튼: <ProfileSetupModal forceOpen onClose={...} />
// ──────────────────────────────────────────────────────────────────────────

const PRESETS: Array<{ id: string; name: string; emoji: string }> = [
  { id: "suhyeon", name: "수현", emoji: "👨‍💻" },
  { id: "wife", name: "수진", emoji: "👰" },
];

const EMOJI_GRID = ["👨", "👩", "👴", "👵", "🧑", "👶", "🐱", "🐶", "🦊", "🐻", "⭐", "🎵"];

type Props = {
  /** true면 항상 노출. 미지정 시 프로필이 없을 때만 노출. */
  forceOpen?: boolean;
  /** 모달이 닫혀야 할 때(저장 완료) 호출 — forceOpen 모드에서만 의미 있음. */
  onClose?: () => void;
};

export function ProfileSetupModal({ forceOpen = false, onClose }: Props) {
  const { profile, setProfile, isLoading } = useProfile();
  const [mode, setMode] = useState<"choose" | "custom">("choose");
  const [customName, setCustomName] = useState("");
  const [customEmoji, setCustomEmoji] = useState<string>(EMOJI_GRID[0]);

  if (isLoading) return null;
  // forceOpen이 아니면 프로필이 있을 때 자동으로 안 보여줌
  if (!forceOpen && profile) return null;

  const commit = (p: { id: string; name: string; emoji: string }) => {
    const next: Profile = {
      id: p.id,
      name: p.name,
      emoji: p.emoji,
      createdAt: new Date().toISOString(),
    };
    setProfile(next);
    onClose?.();
  };

  const pickPreset = (preset: (typeof PRESETS)[number]) => {
    commit(preset);
  };

  const submitCustom = () => {
    const name = customName.trim();
    if (!name) return;
    commit({
      id: `custom-${Date.now()}`,
      name,
      emoji: customEmoji,
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-3xl bg-[var(--surface)] p-6 shadow-2xl">
        <div className="text-center">
          <div className="text-xl font-bold">자비스 프로필</div>
          <div className="mt-1 text-sm text-[var(--muted)]">
            누구인지 알려줘 — 자비스가 너에게 맞춰 답할게
          </div>
        </div>

        {mode === "choose" && (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pickPreset(p)}
                  className={cn(
                    "flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl",
                    "bg-[var(--surface-2)] text-[var(--foreground)] transition active:scale-95",
                  )}
                >
                  <span className="text-4xl">{p.emoji}</span>
                  <span className="text-base font-semibold">{p.name}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setMode("custom")}
              className="mt-3 w-full rounded-2xl border border-dashed border-[var(--border)] px-4 py-3 text-sm font-medium text-[var(--muted)] active:bg-[var(--surface-2)]"
            >
              직접 입력
            </button>
          </>
        )}

        {mode === "custom" && (
          <>
            <div className="mt-6">
              <label className="mb-1.5 block text-xs font-medium text-[var(--muted)]">
                이름
              </label>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="예: 수현"
                maxLength={12}
                className="w-full rounded-2xl bg-[var(--surface-2)] px-4 py-3 text-base outline-none ring-1 ring-transparent focus:ring-[var(--accent)]"
                autoFocus
              />
            </div>
            <div className="mt-4">
              <div className="mb-1.5 text-xs font-medium text-[var(--muted)]">이모지</div>
              <div className="grid grid-cols-6 gap-2">
                {EMOJI_GRID.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setCustomEmoji(e)}
                    className={cn(
                      "flex aspect-square items-center justify-center rounded-xl text-2xl transition active:scale-95",
                      customEmoji === e
                        ? "bg-[var(--accent)]/20 ring-2 ring-[var(--accent)]"
                        : "bg-[var(--surface-2)]",
                    )}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setMode("choose")}
                className="flex-1 rounded-2xl bg-[var(--surface-2)] px-4 py-3 text-sm font-medium text-[var(--muted)] active:opacity-80"
              >
                뒤로
              </button>
              <button
                type="button"
                onClick={submitCustom}
                disabled={!customName.trim()}
                className="flex-[2] rounded-2xl bg-[var(--accent)] px-4 py-3 text-sm font-semibold text-white transition active:opacity-80 disabled:opacity-40"
              >
                저장
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 글로벌 진입점에 한 번 마운트하는 게이트.
 * 프로필이 없을 때만 모달을 띄운다. 있으면 아무것도 렌더하지 않음.
 *
 * /login 또는 비로그인 상태에선 게이트 자체를 무력화 — 인증이 프로필 source of truth.
 */
export function ProfileSetupGate() {
  const pathname = usePathname();
  const { user, loading } = useAuth();
  if (pathname === "/login") return null;
  if (loading) return null;
  if (!user) return null; // 비로그인 — 미들웨어가 곧 /login으로 보냄
  return <ProfileSetupModal />;
}
